import { usePage, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { DashboardPageProps } from '@/types'
import { Building2, ArrowRight, Plus, Users, CheckCircle2, Clock } from 'lucide-react'

interface Formation {
  id: string
  legal_name: string
  entity_name: string
  lifecycle_state: string
  created_at: string
  updated_at: string
}

const stateLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  application: { label: 'In Progress', variant: 'outline' },
  filing: { label: 'Filing', variant: 'secondary' },
  completed: { label: 'Completed', variant: 'default' },
}

export default function DashboardIndex() {
  const { props } = usePage()
  const { auth } = usePage<DashboardPageProps>().props
  const accountSlug = auth.account?.slug

  const stats = props.stats as { total_formations: number; active_formations: number; completed_formations: number; team_members: number } | undefined
  const recent_formations: Formation[] = (props.recent_formations as Formation[]) || []
  const hasFormations = recent_formations.length > 0

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">
              Welcome back, {auth.user?.name?.split(' ')[0]}
            </h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              Here's what's happening in {auth.account?.name}.
            </p>
          </div>
          {hasFormations && (
            <Button asChild size="sm" className="gap-1.5 self-start h-8 text-[13px]">
              <Link href={`/${accountSlug}/formations/new`}>
                <Plus className="w-3.5 h-3.5" />
                New Formation
              </Link>
            </Button>
          )}
        </div>

        {/* Stats */}
        {stats && stats.total_formations > 0 && (
          <div className="flex items-center gap-8">
            {[
              { label: 'Total', value: stats.total_formations, icon: Building2 },
              { label: 'In Progress', value: stats.active_formations, icon: Clock },
              { label: 'Completed', value: stats.completed_formations, icon: CheckCircle2 },
              { label: 'Team Members', value: stats.team_members, icon: Users },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className="text-2xl font-semibold tracking-tight">{stat.value}</span>
                <span className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">{stat.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!hasFormations && (
          <div className="flex flex-col items-center text-center py-14 px-6 border-2 border-dashed border-border/60 rounded-lg">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-base font-semibold mb-1">Start your first formation</h2>
            <p className="text-[13px] text-muted-foreground max-w-sm mb-6">
              Register an LLC or Corporation. We'll handle the paperwork and filing with the state.
            </p>
            <Button asChild size="sm" className="gap-1.5 h-8 text-[13px]">
              <Link href={`/${accountSlug}/formations/new`}>
                <Plus className="w-3.5 h-3.5" />
                New Formation
              </Link>
            </Button>
          </div>
        )}

        {/* Recent formations */}
        {hasFormations && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-foreground">Recent Formations</h2>
              <Link
                href={`/${accountSlug}/formations`}
                className="text-[13px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                View all
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div>
              {recent_formations.map((formation) => {
                const stateInfo = stateLabels[formation.lifecycle_state] || {
                  label: formation.lifecycle_state.replace(/_/g, ' '),
                  variant: 'outline' as const,
                }
                return (
                  <Link
                    key={formation.id}
                    href={`/${accountSlug}/formations/${formation.id}`}
                    className="flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium truncate">{formation.legal_name}</p>
                        <p className="text-[12px] text-muted-foreground truncate">{formation.entity_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      <Badge variant={stateInfo.variant} className="text-[11px] font-medium capitalize">
                        {stateInfo.label}
                      </Badge>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40" />
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
